import { useCallback } from 'react';

import { env } from '@/config/env';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { baseApi } from '@/stores/api';

import { authApi, useLoginMutation, useLogoutMutation } from '../api/authApi';
import { setCredentials, logout as logoutAction } from '../stores/authSlice';
import type { LoginRequest, Permission } from '../types/auth.types';

export function useAuth() {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, isLoading } = useAppSelector((state) => state.auth);
  const [loginMutation, { isLoading: isLoginLoading }] = useLoginMutation();
  const [logoutMutation] = useLogoutMutation();

  const login = useCallback(
    async (credentials: LoginRequest) => {
      const result = await loginMutation(credentials).unwrap();
      dispatch(setCredentials(result));
      if (env.API_BASE_URL) {
        void dispatch(authApi.endpoints.verifySession.initiate()).catch(() => {});
      }
      return result;
    },
    [dispatch, loginMutation],
  );

  const logout = useCallback(async () => {
    try {
      await logoutMutation().unwrap();
    } catch {
      // Proceed with local logout even if API call fails
    } finally {
      dispatch(logoutAction());
      dispatch({ type: 'socket/disconnect' });
      dispatch(baseApi.util.resetApiState());
    }
  }, [dispatch, logoutMutation]);

  const hasPermission = useCallback(
    (permission: Permission) => {
      if (!user) return false;
      if (user.role === 'admin') return true;
      return user.permissions.includes(permission);
    },
    [user],
  );

  return {
    user,
    isAuthenticated,
    isLoading: isLoading || isLoginLoading,
    login,
    logout,
    hasPermission,
  };
}
